import { useCallback, useEffect, useRef, useState } from 'react'
import { useReducedMotion } from 'framer-motion'
import useEmblaCarousel from 'embla-carousel-react'
import { ChevronLeft, ChevronRight, Users } from 'lucide-react'
import { useLanguage } from '../../contexts/LanguageContext'
import { SHOWCASE } from './showcaseData'
import './landing.css'

// How long a frame stays before the slideshow moves on by itself.
const AUTOPLAY_MS = 4500

/**
 * The four example invitations (public/showcase/*.mp4, with their .jpg
 * posters) as a one-frame-at-a-time slideshow in a floating card — the
 * landing hero's screen, reused on the login page's product preview.
 *
 * Advances by itself every AUTOPLAY_MS and on swipe, the arrow buttons, the
 * dots and the keyboard; any navigation restarts the timer, hovering or
 * touching pauses it. Only the visible frame's video plays, and
 * preload="none" keeps every video off the initial page load. Reduced motion
 * turns autoplay off. No bezel: the card floats on its own (`.landing-phone`
 * gives it the slow bob) — rounded corners and the deep shadow are on the
 * screen itself, nothing outlines it.
 */
const ShowcaseSlideshow = () => {
  const { language } = useLanguage()
  const isKa = language === 'ka'
  const lang = isKa ? 'ka' : 'en'
  const reduce = useReducedMotion()

  const [emblaRef, embla] = useEmblaCarousel({ loop: true, align: 'start', duration: 22 })
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const videoRefs = useRef<Array<HTMLVideoElement | null>>([])

  // Track the selected frame.
  useEffect(() => {
    if (!embla) return
    const onSelect = () => setIndex(embla.selectedScrollSnap())
    embla.on('select', onSelect)
    onSelect()
    return () => {
      embla.off('select', onSelect)
    }
  }, [embla])

  // Only the frame on screen plays; the rest sit on their poster.
  useEffect(() => {
    videoRefs.current.forEach((video, i) => {
      if (!video) return
      if (i === index) video.play().catch(() => {})
      else video.pause()
    })
  }, [index])

  // Autoplay. `index` is a dependency on purpose: every change of frame —
  // automatic or by the user — re-arms the timer, so a swipe never gets
  // followed half a second later by the scheduled advance.
  useEffect(() => {
    if (!embla || reduce || paused) return
    const id = window.setInterval(() => embla.scrollNext(), AUTOPLAY_MS)
    return () => window.clearInterval(id)
  }, [embla, reduce, paused, index])

  const prev = useCallback(() => embla?.scrollPrev(), [embla])
  const next = useCallback(() => embla?.scrollNext(), [embla])
  const goTo = useCallback((i: number) => embla?.scrollTo(i), [embla])

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault()
      prev()
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      next()
    }
  }

  return (
    <div
      className="landing-phone relative rounded-[1.7rem] shadow-[0_40px_80px_rgba(27,16,51,0.3)]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      onTouchEnd={() => setPaused(false)}
      onKeyDown={onKeyDown}
      tabIndex={0}
      role="region"
      aria-roledescription="carousel"
      aria-label={isKa ? 'მოსაწვევების ნიმუშები' : 'Invitation examples'}
      data-testid="hero-showcase"
    >
      <div className="relative overflow-hidden rounded-[1.7rem] bg-celebration-ink/5 ring-1 ring-white/40 dark:ring-white/10">
        {/* The frames */}
        <div ref={emblaRef} className="overflow-hidden">
          <div className="flex touch-pan-y">
            {SHOWCASE.map((item, i) => (
              <figure
                key={item.media}
                className="relative min-w-0 shrink-0 grow-0 basis-full"
                aria-label={item.label[lang]}
                aria-hidden={i !== index}
                data-testid={`hero-slide-${i}`}
              >
                <div className="relative aspect-[3/4]">
                  <video
                    ref={(el) => {
                      videoRefs.current[i] = el
                    }}
                    className="absolute inset-0 h-full w-full object-cover"
                    src={`${item.media}.mp4`}
                    poster={`${item.media}.jpg`}
                    muted
                    loop
                    playsInline
                    preload="none"
                    aria-hidden="true"
                  />
                  <figcaption className="absolute inset-x-0 top-0 flex items-start justify-between p-3">
                    <span className="inline-flex items-center gap-1 rounded-full bg-celebration-ink/55 px-2 py-1 text-[10px] font-medium text-white backdrop-blur">
                      <Users className="h-3 w-3" />
                      {item.rsvp} RSVP
                    </span>
                    <span className="rounded-full bg-celebration-ink/55 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-white backdrop-blur">
                      {item.type[lang]}
                    </span>
                  </figcaption>
                </div>
              </figure>
            ))}
          </div>
        </div>

        {/* Dots */}
        <div className="absolute inset-x-0 bottom-3 flex justify-center gap-1.5" role="tablist">
          {SHOWCASE.map((item, i) => (
            <button
              key={item.media}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={`${item.type[lang]} — ${i + 1}/${SHOWCASE.length}`}
              onClick={() => goTo(i)}
              className={`h-2 rounded-full transition-all ${
                i === index ? 'w-6 bg-white' : 'w-2 bg-white/55 hover:bg-white/80'
              }`}
              data-testid={`hero-slide-dot-${i}`}
            />
          ))}
        </div>
      </div>

      {/* Arrows — outside the card, beside it at mid-height, so they never
          cover the invitation. On phones there is only the page margin to
          stand in, so they hug the edge; from sm up they get clear space. */}
      <button
        type="button"
        onClick={prev}
        aria-label={isKa ? 'წინა ნიმუში' : 'Previous example'}
        className="absolute -left-9 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white text-celebration-ink shadow-celebrate-md transition-transform hover:scale-110 sm:-left-12 dark:bg-[#1e1438] dark:text-[#f4f0ff]"
        data-testid="hero-slide-prev"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={next}
        aria-label={isKa ? 'შემდეგი ნიმუში' : 'Next example'}
        className="absolute -right-9 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white text-celebration-ink shadow-celebrate-md transition-transform hover:scale-110 sm:-right-12 dark:bg-[#1e1438] dark:text-[#f4f0ff]"
        data-testid="hero-slide-next"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  )
}

export default ShowcaseSlideshow
